// src/pages/OrderDetail/OrderDetail.jsx
// FRONT OFFICE - Order detail page with WhatsApp payment integration

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaCheckCircle, 
  FaBoxOpen, 
  FaShippingFast, 
  FaPrint, 
  FaWhatsapp,
  FaTimesCircle
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { getOrderById, updateOrderStatus, ORDER_STATUS } from '../../services/OrderService';
import './OrderDetail.css';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  useEffect(() => {
    // Redirect to login if not authenticated
    if (!currentUser) {
      navigate('/login', { 
        state: { 
          from: `/order/${id}`,
          message: 'Please log in to view your order' 
        } 
      });
      return;
    }
    
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        
        if (!id) {
          setError('Order ID is missing');
          return;
        }
        
        const orderData = await getOrderById(id);
        
        // Verify the order belongs to the current user
        if (orderData.userId !== currentUser?.uid) {
          setError('You do not have permission to view this order.');
          navigate('/my-account');
          return;
        }
        
        setOrder(orderData);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to load order details. Please check your order history.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrderDetails();
  }, [id, currentUser, navigate]);
  
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp.seconds * 1000);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  const handlePrintOrder = () => {
    window.print();
  };
  
  const handleContactSupport = () => {
    // Create WhatsApp message for support
    const message = `Hello, I have a question about my order ${id} placed on ${formatDate(order?.createdAt)}.`;
    
    // Replace with your actual WhatsApp support number
    const whatsappNumber = '+491632055734';
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
  };
  
  const handleCompletePayment = () => {
    // Create payment completion message with order details
    const itemsList = order?.items 
      ? order.items.map(item => 
          `${item.product?.name} (${item.quantity} × €${item.price?.toFixed(2) || '0.00'})`
        ).join('\n- ')
      : 'No items available';
    
    const message = `Hello, I would like to complete payment for my order ${order.id} totaling €${order.total.toFixed(2)}.\n\nOrder Details:\n- ${itemsList}\n\nShipping Address: ${order.shippingAddress || 'Not specified'}`;
    
    // WhatsApp number
    const whatsappNumber = '+491632055734';
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
  };
  
  const handleCancelOrder = () => {
    setShowCancelModal(true);
  };
  
  const confirmCancelOrder = async () => {
    try {
      setLoading(true);
      await updateOrderStatus(order.id, ORDER_STATUS.CANCELLED);
      
      // Reload order data after status update
      const updatedOrder = await getOrderById(id);
      setOrder(updatedOrder);
      
      setShowCancelModal(false);
    } catch (err) {
      console.error('Error cancelling order:', err);
      setError('Failed to cancel order. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Loading state
  if (loading && !order) {
    return (
      <div className="order-detail-page">
        <div className="container">
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="order-detail-page">
        <div className="container">
          <div className="error-message">
            <h2>Error</h2>
            <p>{error}</p>
            <Link to="/my-account" className="btn btn-primary">Go to My Account</Link>
          </div>
        </div>
      </div>
    );
  }
  
  if (!order) {
    return (
      <div className="order-detail-page">
        <div className="container">
          <div className="error-message">
            <h2>Order Not Found</h2>
            <p>The order you are looking for does not exist or has been removed.</p>
            <Link to="/my-account" className="btn btn-primary">Go to My Account</Link>
          </div>
        </div>
      </div>
    );
  }
  
  const isOrderCancellable = order.status === ORDER_STATUS.PENDING;
  
  return (
    <div className="order-detail-page">
      <div className="container">
        <div className="order-detail-header">
          <Link to="/my-account" className="back-link">
            <FaArrowLeft /> Back to My Account
          </Link>
          
          <div className="order-title">
            <h1>Order {order.id.substring(0, 8)}</h1>
            <div className="order-meta">
              <span className="order-date">Placed on {formatDate(order.createdAt)}</span>
              <span className={`order-status ${order.status.toLowerCase()}`}>
                {order.status}
              </span>
            </div>
          </div>
        </div>
        
        {order.status === ORDER_STATUS.CANCELLED && (
          <div className="order-cancelled-banner">
            <FaTimesCircle />
            <p>This order has been cancelled</p>
          </div>
        )}
        
        <div className="order-detail-content">
          <div className="order-summary">
            <div className="order-section">
              <h2><FaBoxOpen /> Items Ordered</h2>
              <div className="order-items">
                {order.items && order.items.map((item, index) => (
                  <div key={index} className="order-item">
                    <div className="item-image">
                      <img 
                        src={item.product?.image || "https://via.placeholder.com/60x60?text=Product"} 
                        alt={item.product?.name} 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/60x60?text=Product";
                        }}
                      />
                    </div>
                    <div className="item-details">
                      <h4>{item.product?.name}</h4>
                      <p className="item-meta">
                        <span>Qty: {item.quantity}</span>
                        <span>Price: €{item.price?.toFixed(2) || '0.00'}</span>
                      </p>
                    </div>
                    <div className="item-price">
                      €{((item.price || 0) * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="order-info-grid">
              <div className="order-section">
                <h2><FaShippingFast /> Shipping Information</h2>
                <div className="shipping-details">
                  <p><strong>Shipping Address:</strong></p>
                  <p>{order.shippingAddress || 'No address provided'}</p>
                  
                  <p className="mt-3"><strong>Shipping Method:</strong></p>
                  <p>Standard Shipping</p>
                  
                  <p className="mt-3"><strong>Estimated Delivery:</strong></p>
                  {order.status === ORDER_STATUS.CANCELLED ? (
                    <p className="cancelled-delivery">Delivery Cancelled</p>
                  ) : (
                    <p>3-5 Business Days</p>
                  )}
                </div>
              </div>
              
              <div className="order-section">
                <h2><FaCheckCircle /> Payment Information</h2>
                <div className="payment-details">
                  <p><strong>Payment Method:</strong> {order.paymentMethod || 'WhatsApp'}</p>
                  <p>
                    <strong>Payment Status:</strong> 
                    <span className={`payment-status ${order.paymentStatus || 'pending'}`}>
                      {order.paymentStatus || 'Pending'}
                    </span>
                  </p>
                  
                  {(order.paymentStatus === 'pending' || !order.paymentStatus) && order.status !== ORDER_STATUS.CANCELLED && (
                    <div className="payment-actions">
                      <p>Complete your payment by contacting us via WhatsApp.</p>
                      <button 
                        className="whatsapp-btn"
                        onClick={handleCompletePayment}
                      >
                        <FaWhatsapp /> Complete Payment via WhatsApp
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="order-section">
              <h2>Order Totals</h2>
              <div className="order-totals">
                <div className="totals-row">
                  <span>Subtotal:</span>
                  <span>€{order.subtotal.toFixed(2)}</span>
                </div>
                <div className="totals-row">
                  <span>Shipping:</span>
                  <span>{order.shippingCost > 0 ? `€${order.shippingCost.toFixed(2)}` : 'FREE'}</span>
                </div>
                <div className="totals-row grand-total">
                  <span>Total:</span>
                  <span>€{order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="order-actions">
          <div className="action-buttons">
            <button className="btn btn-outline" onClick={handlePrintOrder}>
              <FaPrint /> Print Order
            </button>
            <button className="btn btn-outline" onClick={handleContactSupport}>
              <FaWhatsapp /> Contact Support
            </button>
            
            {isOrderCancellable && (
              <button className="btn btn-danger" onClick={handleCancelOrder}>
                <FaTimesCircle /> Cancel Order
              </button>
            )}
          </div>
          
          <Link to="/shop" className="btn btn-primary">
            Continue Shopping
          </Link>
        </div>
      </div>
      
      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Cancel Order</h3>
              <button className="modal-close" onClick={() => setShowCancelModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to cancel this order?</p>
              <p>Order {order.id.substring(0, 8)} - Total: €{order.total.toFixed(2)}</p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowCancelModal(false)}
                disabled={loading}
              >
                Keep Order
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmCancelOrder}
                disabled={loading}
              >
                {loading ? 'Cancelling...' : 'Yes, Cancel Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;